import React, { useEffect, useState } from "react";
import { Col, Row, Form, Button, Modal } from "react-bootstrap";
import Switch from "react-input-switch";
import NumberFormat from "react-number-format";
import Layout from "../../HomeLayout/Layout";
import "./bankGuarantee.css";
import UploadDoc from "../../assests/upload.png";
import {
  getFilePreview,
  processFileUpload,
  validateDate,
} from "../../utils/utils";
import { FaDownload, FaEye } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";
import LoaderModal from "../NewOnBoarding/LoaderModal";
import AutocompleteSelect from "../common/AutocompleteSelect";
import { RiDeleteBack2Fill } from "react-icons/ri";
import { set } from "lodash";
import { IoDownload } from "react-icons/io5";
import {
  Api,
  deleteDocById,
  uploadOrUpdateDocuments,
} from "../../services/httpService";
import moment from "moment/moment";
import { use } from "react";
import { useNavigate, useLocation } from "react-router";

const BankGuarantee = ({ skipLayout = false, onBack, editProject = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const merchantId = localStorage?.getItem("_id");
  const merchantName = localStorage?.getItem("_name");
  const defCompanyName = localStorage?.getItem("defCompanyName");
  
  // Check if we're on the onboarding page (select-product)
  const isOnboardingPage = skipLayout || location.pathname.includes("select-product");
  
  const formatDateForInput = (value) => {
    if (!value) {
      return "";
    }
    const formatted = moment(value).format("YYYY-MM-DD");
    return formatted === "Invalid date" ? "" : formatted;
  };

  const fetchExistingDocument = async (projectId, documentId) => {
    if (!projectId || !documentId) {
      return null;
    }

    try {
      const response = await Api(
        "GET",
        `/documents/getDocumentById/${projectId}/BG_PROJECT/${documentId}`
      );

      if (response?.success && response?.body?.file) {
        const fileInfo = response.body.file;
        return {
          name: fileInfo.fileName || `Document-${documentId}`,
          fileName: fileInfo.fileName,
          fileType: fileInfo.fileType,
          fileId: fileInfo.id,
          docId: response.body.docId ?? documentId,
        };
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }

    return null;
  };

  const getInitialDetails = () => {
    if (editProject) {
      const isApgApplicable =
        typeof editProject.isNotMandatoryApg === "boolean"
          ? !editProject.isNotMandatoryApg
          : !!editProject.isApgApplicable || !!editProject.apgPercent;
      const isPbgApplicable =
        typeof editProject.isNotMandatoryPbg === "boolean"
          ? !editProject.isNotMandatoryPbg
          : !!editProject.isPbgApplicable || !!editProject.pbgPercent;

      // Populate form with existing project data for editing
      return {
        merchantId,
        projectId: editProject.id,
        isOpenEnded: !editProject.projectEndDate,
        projectName: editProject.projectName || "",
        projectValue: editProject.projectValue ? String(editProject.projectValue) : "",
        projectReferenceNumber: editProject.projectReferenceNumber || "",
        counterparty: editProject.counterpartyId ?? editProject.counterparty?.id ?? editProject.counterparty ?? "",
        projectStartDate: formatDateForInput(editProject.projectStartDate),
        projectEndDate: formatDateForInput(editProject.projectEndDate),
        agreementDocument: editProject.agreementDocument || [],
        cashFlowDocument: editProject.cashFlowDocument || [],
        additionalDocuments: editProject.additionalDocuments || [],
        agreementDocumentIds: editProject.agreementDocumentIds || (editProject.uploadProjectAgreementDocumentId ? [editProject.uploadProjectAgreementDocumentId] : []),
        cashFlowDocumentIds: editProject.cashFlowDocumentIds || (editProject.uploadProjectCashflowsDocumentId ? [editProject.uploadProjectCashflowsDocumentId] : []),
        additionalDocumentIds: editProject.additionalDocumentIds || [],
        uploadApgLetterDocumentId: editProject.uploadApgLetterDocumentId ?? null,
        uploadPbgLetterDocumentId: editProject.uploadPbgLetterDocumentId ?? null,
        status: editProject.status ?? null,
        projectApprovalDate: editProject.projectApprovalDate ?? null,
        zeloBgIssuanceDate: editProject.zeloBgIssuanceDate ?? null,
        advancePaymentCashSweepPercent: editProject.advancePaymentCashSweepPercent ?? null,
        invoicePaymentCashSweepPercent: editProject.invoicePaymentCashSweepPercent ?? null,
        initialCollectedCashMarginPercent: editProject.initialCollectedCashMarginPercent ?? null,
        platformFeePercentPerDay: editProject.platformFeePercentPerDay ?? null,
        isApgApplicable,
        apgAmountMode:
          editProject.apgAmountMode === "FIXED" ||
          (editProject.apgFixedAmount != null && editProject.apgFixedAmount !== "" && Number(editProject.apgFixedAmount) > 0)
            ? "FIXED"
            : "PERCENT",
        apgFixedAmount:
          editProject.apgAmountMode === "FIXED" || (editProject.apgFixedAmount != null && editProject.apgFixedAmount !== "" && Number(editProject.apgFixedAmount) > 0)
            ? String(editProject.apgFixedAmount ?? "")
            : "",
        apgPercentage: editProject.apgPercentage
          ? String(editProject.apgPercentage)
          : editProject.apgPercent
            ? String(editProject.apgPercent)
            : "",
        apgStartDate: formatDateForInput(editProject.apgStartDate),
        apgEndDate: formatDateForInput(editProject.apgEndDate),
        expectedAdvancePaymentDate: formatDateForInput(
          editProject.expectedAdvancePaymentDate || editProject.apgExpectedPaymentDate
        ),
        isPbgApplicable,
        pbgAmountMode:
          editProject.pbgAmountMode === "FIXED" ||
          (editProject.pbgFixedAmount != null && editProject.pbgFixedAmount !== "" && Number(editProject.pbgFixedAmount) > 0)
            ? "FIXED"
            : "PERCENT",
        pbgFixedAmount:
          editProject.pbgAmountMode === "FIXED" || (editProject.pbgFixedAmount != null && editProject.pbgFixedAmount !== "" && Number(editProject.pbgFixedAmount) > 0)
            ? String(editProject.pbgFixedAmount ?? "")
            : "",
        pbgExpiryMode:
          editProject.pbgExpiryMode === "DLP" ||
          (editProject.defectsLiabilityPeriodMonths != null && String(editProject.defectsLiabilityPeriodMonths).trim() !== "")
            ? "DLP"
            : "MANUAL",
        defectsLiabilityPeriodMonths:
          editProject.defectsLiabilityPeriodMonths != null && String(editProject.defectsLiabilityPeriodMonths).trim() !== ""
            ? String(editProject.defectsLiabilityPeriodMonths)
            : "",
        pbgPercentage: editProject.pbgPercentage
          ? String(editProject.pbgPercentage)
          : editProject.pbgPercent
            ? String(editProject.pbgPercent)
            : "",
        pbgStartDate: formatDateForInput(editProject.pbgStartDate),
        pbgEndDate: formatDateForInput(editProject.pbgEndDate),
        agreeToTerms2: editProject.agreeToTerms2 || false,
      };
    }
    return {
      merchantId,
      isOpenEnded: false,
      projectName: "",
      projectValue: "",
      projectReferenceNumber: "",
        counterparty: "",
      projectStartDate: "",
      projectEndDate: "",
      agreementDocument: [],
      cashFlowDocument: [],
      additionalDocuments: [],
      agreementDocumentIds: [],
      cashFlowDocumentIds: [],
      additionalDocumentIds: [],
      isApgApplicable: false,
      apgAmountMode: "PERCENT",
      apgFixedAmount: "",
      apgPercentage: "",
      apgStartDate: "",
      apgEndDate: "",
      expectedAdvancePaymentDate: "",
      isPbgApplicable: false,
      pbgAmountMode: "PERCENT",
      pbgFixedAmount: "",
      pbgExpiryMode: "MANUAL",
      defectsLiabilityPeriodMonths: "",
      pbgPercentage: "",
      pbgStartDate: "",
      pbgEndDate: "",
      // agreeToTerms1: false,
      agreeToTerms2: false,
      // agreeToTerms3: false,
    };
  };

  const initialBankGuaranteeDetails = getInitialDetails();
  const [counterpartyOptions, setCounterpartyOptions] = useState([]);
  const [isValidForm, setIsValidForm] = useState(false);
  const [bankGuaranteeDetails, setBankGuaranteeDetails] = useState(
    initialBankGuaranteeDetails
  );
  const [showPreview, setShowPreview] = useState(false);
  const [currentPreview, setCurrentPreview] = useState({ url: "", name: "" });
  const [loadings, setLoadings] = useState({
    preview: false,
    agreementDocument: false,
    cashFlowDocument: false,
    additionalDocuments: false,
    submit: false,
  });
  const [errors, setErrors] = useState({ preview: "" });

  const checkFormValid = () => {
    const {
      isOpenEnded,
      projectName,
      projectValue,
      projectReferenceNumber,
      counterparty,
      projectStartDate,
      projectEndDate,
      agreementDocument,
      cashFlowDocument,
      isApgApplicable,
      apgAmountMode,
      apgPercentage,
      apgFixedAmount,
      expectedAdvancePaymentDate,
      isPbgApplicable,
      pbgAmountMode,
      pbgPercentage,
      pbgFixedAmount,
      pbgEndDate,
      pbgExpiryMode,
      defectsLiabilityPeriodMonths,
      // agreeToTerms1,
      agreeToTerms2,
      // agreeToTerms3,
    } = bankGuaranteeDetails;

    const fail = (msg) => {
      console.log("Validation failed:", msg);
      setIsValidForm(false);
      return false;
    };

    // Project Name: 2–255 chars (allow special characters, including commas)
    const projectNameTrimmed = String(projectName || "").trim();
    if (projectNameTrimmed.length < 2 || projectNameTrimmed.length > 255) {
      return fail("Invalid project name");
    }

    if (
      projectReferenceNumber &&
      String(projectReferenceNumber).trim().length > 256
    ) {
      return fail("Project reference number exceeds 256 characters");
    }

    // Project Value: formatted UI but payload should be unformatted numeric value
    // Required only when APG or PBG uses "% of Project Value"
    const projectValueStr = String(projectValue || "").trim();
    const projectValueNum =
      projectValueStr === "" ? NaN : Number(projectValueStr);
    const isProjectValueRequired =
      (isApgApplicable && apgAmountMode === "PERCENT") ||
      (isPbgApplicable && pbgAmountMode === "PERCENT");
    if (isProjectValueRequired) {
      if (!Number.isFinite(projectValueNum) || projectValueNum <= 0) {
        return fail("Project value is required for percentage-based guarantees");
      }
    } else if (projectValueStr !== "") {
      if (!Number.isFinite(projectValueNum) || projectValueNum <= 0) {
        return fail("Invalid project value");
      }
    }

    // Counterparty: required
    const counterpartyStr = String(counterparty || "").trim();
    if (!counterpartyStr) {
      return fail("Counterparty is required");
    }

    // Project Start Date: allow historical dates
    const startDate = moment(projectStartDate);
    if (!startDate.isValid()) {
      return fail("Invalid project start date");
    }

    // Project End Date: based on project type
    const endDate = moment(projectEndDate);
    if (!isOpenEnded) {
      if (!endDate.isValid() || endDate.isBefore(startDate)) {
        return fail("Invalid or before start date for project end date");
      }
    } else {
      // OPEN_ENDED: end date optional; if present, must be >= start date
      if (String(projectEndDate || "").trim() !== "") {
        if (!endDate.isValid() || endDate.isBefore(startDate)) {
          return fail("Invalid or before start date for project end date");
        }
      }
    }

    // File uploads mandatory
    if (!agreementDocument?.length || !cashFlowDocument?.length) {
      // uncomment this
      return fail("Missing required document(s)");
    }

    // APG Validation
    if (isApgApplicable) {
      const expectedAdvance = moment(expectedAdvancePaymentDate);

      if (apgAmountMode === "PERCENT") {
        const apgPercentageStr = String(apgPercentage || "").trim();
        if (
          !/^(100(\.0{1,2})?|[0-9]?\d(\.\d{1,2})?)$/.test(apgPercentageStr)
        ) {
          return fail(
            "APG percentage must be between 0 and 100, up to 2 decimals"
          );
        }
        if (!Number.isFinite(projectValueNum) || projectValueNum <= 0) {
          return fail("Project value is required for % based APG amount");
        }
      } else {
        const apgFixedStr = String(apgFixedAmount || "").trim();
        const apgFixedNum = apgFixedStr === "" ? NaN : Number(apgFixedStr);
        if (!Number.isFinite(apgFixedNum) || apgFixedNum <= 0) {
          return fail("APG fixed amount is required");
        }
      }

      if (!expectedAdvance.isValid() || expectedAdvance.isBefore(startDate)) {
        return fail("Invalid expected advance payment date");
      }
    }

    // PBG Validation
    if (isPbgApplicable) {
      if (pbgAmountMode === "PERCENT") {
        const pbgPercentageStr = String(pbgPercentage || "").trim();
        if (
          !/^(100(\.0{1,2})?|[0-9]?\d(\.\d{1,2})?)$/.test(pbgPercentageStr)
        ) {
          return fail(
            "PBG percentage must be between 0 and 100, up to 2 decimals"
          );
        }
        if (!Number.isFinite(projectValueNum) || projectValueNum <= 0) {
          return fail("Project value is required for % based PBG amount");
        }
      } else {
        const pbgFixedStr = String(pbgFixedAmount || "").trim();
        const pbgFixedNum = pbgFixedStr === "" ? NaN : Number(pbgFixedStr);
        if (!Number.isFinite(pbgFixedNum) || pbgFixedNum <= 0) {
          return fail("PBG fixed amount is required");
        }
      }

      if (pbgExpiryMode === "DLP") {
        const dlpStr = String(defectsLiabilityPeriodMonths || "").trim();
        const dlpNum = dlpStr === "" ? NaN : Number(dlpStr);
        if (!Number.isFinite(dlpNum) || dlpNum <= 0) {
          return fail("Defects Liability Period (months) is required");
        }
        if (!endDate.isValid()) {
          return fail("Project end date is required to infer PBG expiry using DLP");
        }
      } else {
        const pbgEnd = moment(pbgEndDate);
        if (!pbgEnd.isValid() || pbgEnd.isBefore(startDate)) {
          return fail("Invalid PBG end date");
        }
      }
    }

    if (!isApgApplicable && !isPbgApplicable) {
      return fail("At least one guarantee type must be applicable");
    }
    //  || !agreeToTerms3 !agreeToTerms1 ||
    if (!agreeToTerms2) {
      return fail("All terms must be agreed");
    }

    setIsValidForm(true);
    console.log("All validations passed");
  };

  const fetchCounterParties = async () => {
    try {
      console.log("Fetching counterparties for merchantId:", merchantId, "isOnboarding:", isOnboardingPage);
      
      let response;
      if (isOnboardingPage) {
        // Use onboarding API for select-product page
        response = await Api(
          "get",
          `/bankGuarantee/v1/checkBuyerPlatformBgAvailability/${merchantId}`
        );
      } else {
        // Use regular API for bank-guarantee-project page
        response = await Api(
          "get",
          `/bankGuarantee/v1/activeCounterparty/${merchantId}`
        );
      }
      
      console.log("Counterparties API response:", response);
      if (response && response.success) {
        const options = response.body || [];
        console.log("Setting counterparty options:", options);
        setCounterpartyOptions(options);
      } else {
        console.error("Error fetching counterparties:", response?.message);
        setCounterpartyOptions([]);
      }
    } catch (error) {
      console.error("Error fetching counterparties:", error);
      setCounterpartyOptions([]);
    }
  };

  useEffect(() => {
    if (merchantId) {
      fetchCounterParties();
    }
  }, [merchantId, isOnboardingPage]);

  useEffect(() => {
    checkFormValid();
  }, [bankGuaranteeDetails]);

  // Populate form when editing an existing project
  useEffect(() => {
    if (editProject && editProject.id) {
      const isApgApplicable =
        typeof editProject.isNotMandatoryApg === "boolean"
          ? !editProject.isNotMandatoryApg
          : !!editProject.isApgApplicable || !!editProject.apgPercent;
      const isPbgApplicable =
        typeof editProject.isNotMandatoryPbg === "boolean"
          ? !editProject.isNotMandatoryPbg
          : !!editProject.isPbgApplicable || !!editProject.pbgPercent;

      const editDetails = {
        merchantId,
        projectId: editProject.id,
        isOpenEnded: !editProject.projectEndDate,
        projectName: editProject.projectName || "",
        projectValue: editProject.projectValue ? String(editProject.projectValue) : "",
        counterparty: editProject.counterpartyId ?? editProject.counterparty?.id ?? editProject.counterparty ?? "",
        projectStartDate: formatDateForInput(editProject.projectStartDate),
        projectEndDate: formatDateForInput(editProject.projectEndDate),
        agreementDocument: editProject.agreementDocument || [],
        cashFlowDocument: editProject.cashFlowDocument || [],
        additionalDocuments: editProject.additionalDocuments || [],
        agreementDocumentIds: editProject.agreementDocumentIds || (editProject.uploadProjectAgreementDocumentId ? [editProject.uploadProjectAgreementDocumentId] : []),
        cashFlowDocumentIds: editProject.cashFlowDocumentIds || (editProject.uploadProjectCashflowsDocumentId ? [editProject.uploadProjectCashflowsDocumentId] : []),
        additionalDocumentIds: editProject.additionalDocumentIds || [],
        uploadApgLetterDocumentId: editProject.uploadApgLetterDocumentId ?? null,
        uploadPbgLetterDocumentId: editProject.uploadPbgLetterDocumentId ?? null,
        status: editProject.status ?? null,
        projectApprovalDate: editProject.projectApprovalDate ?? null,
        zeloBgIssuanceDate: editProject.zeloBgIssuanceDate ?? null,
        advancePaymentCashSweepPercent: editProject.advancePaymentCashSweepPercent ?? null,
        invoicePaymentCashSweepPercent: editProject.invoicePaymentCashSweepPercent ?? null,
        initialCollectedCashMarginPercent: editProject.initialCollectedCashMarginPercent ?? null,
        platformFeePercentPerDay: editProject.platformFeePercentPerDay ?? null,
        isApgApplicable,
        apgAmountMode:
          editProject.apgAmountMode === "FIXED" ||
          (editProject.apgFixedAmount != null && editProject.apgFixedAmount !== "" && Number(editProject.apgFixedAmount) > 0)
            ? "FIXED"
            : "PERCENT",
        apgFixedAmount:
          editProject.apgAmountMode === "FIXED" || (editProject.apgFixedAmount != null && editProject.apgFixedAmount !== "" && Number(editProject.apgFixedAmount) > 0)
            ? String(editProject.apgFixedAmount ?? "")
            : "",
        apgPercentage: editProject.apgPercentage
          ? String(editProject.apgPercentage)
          : editProject.apgPercent
            ? String(editProject.apgPercent)
            : "",
        apgStartDate: formatDateForInput(editProject.apgStartDate),
        apgEndDate: formatDateForInput(editProject.apgEndDate),
        expectedAdvancePaymentDate: formatDateForInput(
          editProject.expectedAdvancePaymentDate || editProject.apgExpectedPaymentDate
        ),
        isPbgApplicable,
        pbgAmountMode:
          editProject.pbgAmountMode === "FIXED" ||
          (editProject.pbgFixedAmount != null && editProject.pbgFixedAmount !== "" && Number(editProject.pbgFixedAmount) > 0)
            ? "FIXED"
            : "PERCENT",
        pbgFixedAmount:
          editProject.pbgAmountMode === "FIXED" || (editProject.pbgFixedAmount != null && editProject.pbgFixedAmount !== "" && Number(editProject.pbgFixedAmount) > 0)
            ? String(editProject.pbgFixedAmount ?? "")
            : "",
        pbgExpiryMode:
          editProject.pbgExpiryMode === "DLP" ||
          (editProject.defectsLiabilityPeriodMonths != null && String(editProject.defectsLiabilityPeriodMonths).trim() !== "")
            ? "DLP"
            : "MANUAL",
        defectsLiabilityPeriodMonths:
          editProject.defectsLiabilityPeriodMonths != null && String(editProject.defectsLiabilityPeriodMonths).trim() !== ""
            ? String(editProject.defectsLiabilityPeriodMonths)
            : "",
        pbgPercentage: editProject.pbgPercentage
          ? String(editProject.pbgPercentage)
          : editProject.pbgPercent
            ? String(editProject.pbgPercent)
            : "",
        pbgStartDate: formatDateForInput(editProject.pbgStartDate),
        pbgEndDate: formatDateForInput(editProject.pbgEndDate),
        agreeToTerms2: editProject.agreeToTerms2 || false,
      };
      setBankGuaranteeDetails(editDetails);
    }
  }, [editProject]);

  useEffect(() => {
    if (!editProject || !editProject.id) {
      return;
    }

    const agreementDocId =
      editProject.uploadProjectAgreementDocumentId ||
      editProject.agreementDocumentIds?.[0];
    const cashFlowDocId =
      editProject.uploadProjectCashflowsDocumentId ||
      editProject.cashFlowDocumentIds?.[0];

    const shouldLoadAgreement =
      agreementDocId && bankGuaranteeDetails.agreementDocument.length === 0;
    const shouldLoadCashFlow =
      cashFlowDocId && bankGuaranteeDetails.cashFlowDocument.length === 0;

    if (!shouldLoadAgreement && !shouldLoadCashFlow) {
      return;
    }

    if (shouldLoadAgreement) {
      setLoadings((prev) => ({ ...prev, agreementDocument: true }));
    }
    if (shouldLoadCashFlow) {
      setLoadings((prev) => ({ ...prev, cashFlowDocument: true }));
    }

    const loadExistingDocs = async () => {
      const [agreementFile, cashFlowFile] = await Promise.all([
        shouldLoadAgreement
          ? fetchExistingDocument(editProject.id, agreementDocId)
          : Promise.resolve(null),
        shouldLoadCashFlow
          ? fetchExistingDocument(editProject.id, cashFlowDocId)
          : Promise.resolve(null),
      ]);

      setBankGuaranteeDetails((prev) => ({
        ...prev,
        agreementDocument:
          agreementFile && shouldLoadAgreement
            ? [agreementFile]
            : prev.agreementDocument,
        agreementDocumentIds:
          agreementFile && shouldLoadAgreement
            ? [agreementDocId]
            : prev.agreementDocumentIds,
        cashFlowDocument:
          cashFlowFile && shouldLoadCashFlow
            ? [cashFlowFile]
            : prev.cashFlowDocument,
        cashFlowDocumentIds:
          cashFlowFile && shouldLoadCashFlow
            ? [cashFlowDocId]
            : prev.cashFlowDocumentIds,
      }));

      if (shouldLoadAgreement) {
        setLoadings((prev) => ({ ...prev, agreementDocument: false }));
      }
      if (shouldLoadCashFlow) {
        setLoadings((prev) => ({ ...prev, cashFlowDocument: false }));
      }
    };

    loadExistingDocs();
  }, [
    editProject,
    bankGuaranteeDetails.agreementDocument.length,
    bankGuaranteeDetails.cashFlowDocument.length,
  ]);

  // Sync additionalDocumentIds from additionalDocuments whenever it changes
  useEffect(() => {
    const additionalDocs = bankGuaranteeDetails.additionalDocuments;
    
    if (additionalDocs && additionalDocs.length > 0) {
      const docIds = additionalDocs
        .map((file) => file.docId)
        .filter((id) => id != null && id !== undefined && id !== "null" && id !== "")
        .map((id) => Number(id));
      
      // Only update if the IDs are different to avoid infinite loops
      setBankGuaranteeDetails((prev) => {
        const currentIds = prev.additionalDocumentIds || [];
        const idsMatch = docIds.length === currentIds.length && 
                         docIds.every((id, index) => id === currentIds[index]);
        
        if (!idsMatch && docIds.length > 0) {
          console.log("Syncing additionalDocumentIds from additionalDocuments:", docIds);
          return {
            ...prev,
            additionalDocumentIds: docIds,
          };
        }
        return prev;
      });
    } else if (!additionalDocs || additionalDocs.length === 0) {
      // Clear IDs if documents array is empty
      setBankGuaranteeDetails((prev) => {
        if (prev.additionalDocumentIds && prev.additionalDocumentIds.length > 0) {
          console.log("Clearing additionalDocumentIds (no documents)");
          return {
            ...prev,
            additionalDocumentIds: [],
          };
        }
        return prev;
      });
    }
  }, [bankGuaranteeDetails.additionalDocuments]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    if (name.endsWith("Date")) {
      value = validateDate(value);
    } else if (name.endsWith("Percentage")) {
      // Allow only digits + optional decimal part (0-2 decimals)
      if (!/^\d{0,3}(\.\d{0,2})?$/.test(value) && value !== "") return;

      // Convert to number safely
      let num = parseFloat(value);

      // If number exceeds 100 → clamp it to 100
      if (!isNaN(num) && num > 100) {
        num = 100;
        value = num.toString();
      }
    } else if (name === "projectName") {
      // allow special characters (including commas); normalize whitespace and leading spaces only
      value = String(value || "").replace(/\s{2,}/g, " ").trimStart();
    } else if (name === "projectReferenceNumber") {
      // allow common reference separators; normalize whitespace and leading spaces only
      value = String(value || "")
        .replace(/[^a-zA-Z0-9 _./-]/g, "")
        .replace(/\s{2,}/g, " ")
        .trimStart();
    } else if (name === "defectsLiabilityPeriodMonths") {
      // digits only (months)
      value = String(value || "").replace(/[^\d]/g, "");
    } else {
      value = value
        ?.replace(/[^a-zA-Z0-9 ]/g, "")
        ?.replace(/\s{2,}/g, " ")
        ?.trimStart();
    }

    setBankGuaranteeDetails({
      ...bankGuaranteeDetails,
      [name]: value,
    });
  };

  const handleSwitchChange = (name, value) => {
    setBankGuaranteeDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { id, checked } = e.target;
    setBankGuaranteeDetails((prev) => ({
      ...prev,
      [id]: checked,
    }));
  };

  const handleFileUpload = async (e, name) => {
    let validTypes;
    let maxFiles;
    let maxSizeMB;
    let errorMessage;

    if (name === "agreementDocument") {
      validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      maxFiles = 1;
      maxSizeMB = 50;
      errorMessage = "File rejected. Please upload PDF or DOCX files under 50 MB size.";
    } else if (name === "cashFlowDocument") {
      validTypes = [
        "application/vnd.ms-excel", // .xls
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      maxFiles = 1;
      maxSizeMB = 50;
      errorMessage = "File rejected. Please upload XLS or XLSX files under 50 MB size.";
    } else if (name === "additionalDocuments") {
      validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
      ];
      maxFiles = 5;
      maxSizeMB = 50;
      errorMessage = "File rejected. Please upload PDF, JPG, or PNG files under 50 MB size. Maximum 5 files allowed.";
    }

    const { updatedFiles, rejected } = processFileUpload({
      event: e,
      existingFiles: bankGuaranteeDetails[name],
      validTypes,
      maxFiles,
      maxSizeMB,
    });

    if (rejected) {
      alert(errorMessage);
      return;
    }

    console.log(`Before upload - existing files for ${name}:`, bankGuaranteeDetails[name]);
    console.log(`Before upload - existing ${name}Ids:`, bankGuaranteeDetails[`${name}Ids`]);
    console.log(`Updated files to upload for ${name}:`, updatedFiles);

    const docInfoId = name === "agreementDocument" ? 24 : name === "cashFlowDocument" ? 25 : 30; // 30 for additional documents
    try {
      if (updatedFiles.length > 0) {
        setLoadings((prev) => ({ ...prev, [name]: true }));
        const { success, files, response } = await uploadOrUpdateDocuments({
          documentInfoId: docInfoId,
          merchantId,
          uploadedFiles: updatedFiles,
          entityType: "BG_PROJECT",
          // entityId: props?.companyDetails?.id,
        });
        if (success) {
          console.log(`Upload response for ${name}:`, response);
          console.log(`Response body for ${name}:`, response?.body);
          console.log(`Files after upload for ${name}:`, files);
          
          // Extract docIds directly from the files array
          // Each file object should have a docId property after upload
          const extractedDocIds = files
            .map((file) => {
              const docId = file.docId;
              console.log(`File: ${file.name}, docId: ${docId}, type: ${typeof docId}`);
              return docId;
            })
            .filter((id) => {
              // Filter out null, undefined, empty string, and "null" string
              const isValid = id != null && 
                             id !== undefined && 
                             id !== "" && 
                             id !== "null" && 
                             !isNaN(Number(id));
              if (!isValid) {
                console.warn(`Filtered out invalid docId:`, id);
              }
              return isValid;
            })
            .map((id) => Number(id)); // Convert to number to ensure consistency
          
          console.log(`Extracted docIds for ${name}:`, extractedDocIds);
          console.log(`Number of files: ${files.length}, Number of docIds: ${extractedDocIds.length}`);
          
          // Ensure we have the same number of docIds as files
          if (extractedDocIds.length !== files.length) {
            console.warn(`Mismatch: ${files.length} files but only ${extractedDocIds.length} docIds extracted`);
            // Try to get docIds from response.body as fallback
            if (response && response.body && Array.isArray(response.body)) {
              const responseDocIds = response.body
                .map((doc) => doc.docId)
                .filter((id) => id != null && id !== undefined && id !== "null" && id !== "")
                .map((id) => Number(id));
              console.log(`DocIds from response.body:`, responseDocIds);
              
              // Merge both sources
              const mergedDocIds = [...new Set([...extractedDocIds, ...responseDocIds])];
              console.log(`Merged docIds:`, mergedDocIds);
              
              setBankGuaranteeDetails((prev) => {
                console.log(`Updating ${name}Ids to:`, mergedDocIds);
                return {
                  ...prev,
                  [name]: files,
                  [`${name}Ids`]: mergedDocIds,
                };
              });
              return;
            }
          }
          
          // Update state with extracted docIds
          setBankGuaranteeDetails((prev) => {
            console.log(`Previous ${name}Ids:`, prev[`${name}Ids`]);
            console.log(`Updating ${name}Ids to:`, extractedDocIds);
            
            return {
              ...prev,
              [name]: files,
              [`${name}Ids`]: extractedDocIds,
            };
          });
        } else {
          alert(response?.message || "File upload failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setLoadings((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handlePreview = async (file) => {
    setLoadings((prev) => ({ ...prev, preview: true }));
    try {
      const { success, preview } = await getFilePreview(file);
      if (success && preview) {
        setCurrentPreview(preview);
        setShowPreview(true);
      }
    } catch (error) {
      console.error("Error generating file preview:", error);
    } finally {
      setLoadings((prev) => ({ ...prev, preview: false }));
    }
  };

  const handleDeleteFile = async (file, name) => {
    const deleteFile = await deleteDocById(file.docId);
    if (deleteFile) {
      setBankGuaranteeDetails((prev) => {
        const updatedFiles = prev[name].filter((f) => f.docId !== file.docId);
        const updatedIds = prev[`${name}Ids`]?.filter(
          (id) => id !== file?.docId
        );
        return {
          ...prev,
          [name]: updatedFiles,
          [`${name}Ids`]: updatedIds,
        };
      });
    } else {
      console.error("Error deleting file:", file.name);
      return;
    }
  };
  const handleCancel = (e) => {
    e.preventDefault();
    setLoadings((prev) => ({ ...prev, submit: false }));
    setBankGuaranteeDetails(initialBankGuaranteeDetails);
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log("=== SUBMIT - Full bankGuaranteeDetails state ===");
    console.log("bankGuaranteeDetails:", bankGuaranteeDetails);
    console.log("additionalDocuments:", bankGuaranteeDetails.additionalDocuments);
    console.log("additionalDocumentIds:", bankGuaranteeDetails.additionalDocumentIds);
    console.log("agreementDocumentIds:", bankGuaranteeDetails.agreementDocumentIds);
    console.log("cashFlowDocumentIds:", bankGuaranteeDetails.cashFlowDocumentIds);
    
    let { agreementDocument, cashFlowDocument, additionalDocuments, projectId, ...payload } =
      bankGuaranteeDetails;

    console.log("=== After destructuring ===");
    console.log("payload.additionalDocumentIds:", payload?.additionalDocumentIds);

    const toEpochMs = (dateStr) => {
      const s = String(dateStr || "").trim();
      if (!s) return null;
      const m = moment.utc(s, "YYYY-MM-DD", true);
      return m.isValid() ? m.valueOf() : null;
    };

    const projectValueNum =
      payload?.projectValue && String(payload.projectValue).trim() !== ""
        ? Number(payload.projectValue)
        : null;
    const projectValue =
      typeof projectValueNum === "number" && Number.isFinite(projectValueNum)
        ? projectValueNum
        : null;

    const projectStartDate = toEpochMs(payload?.projectStartDate);
    const projectEndDate = toEpochMs(payload?.projectEndDate);

    const apgPercentNum =
      payload?.apgPercentage && String(payload.apgPercentage).trim() !== ""
        ? Number(payload.apgPercentage)
        : null;
    const apgFixedAmountNum =
      payload?.apgFixedAmount && String(payload.apgFixedAmount).trim() !== ""
        ? Number(payload.apgFixedAmount)
        : null;

    const pbgPercentNum =
      payload?.pbgPercentage && String(payload.pbgPercentage).trim() !== ""
        ? Number(payload.pbgPercentage)
        : null;
    const pbgFixedAmountNum =
      payload?.pbgFixedAmount && String(payload.pbgFixedAmount).trim() !== ""
        ? Number(payload.pbgFixedAmount)
        : null;

    const defectsLiabilityPeriodMonthsNum =
      payload?.defectsLiabilityPeriodMonths &&
      String(payload.defectsLiabilityPeriodMonths).trim() !== ""
        ? Number(payload.defectsLiabilityPeriodMonths)
        : null;

    const pbgEndDate =
      payload?.isPbgApplicable && payload?.pbgExpiryMode === "DLP"
        ? null
        : toEpochMs(payload?.pbgEndDate);

    payload = {
      merchantId,
      merchantName: merchantName,
      companyName: defCompanyName,
      projectType: payload?.isOpenEnded ? "OPEN" : "CLOSED",
      projectName: payload?.projectName.trim(),
      projectReferenceNumber:
        String(payload?.projectReferenceNumber || "").trim() || null,
      projectValue: projectValue,
      counterpartyId: payload?.counterparty,
      projectStartDate: projectStartDate,
      projectEndDate: projectEndDate,
      uploadProjectAgreementDocumentId: payload?.agreementDocumentIds?.[0],
      uploadProjectCashflowsDocumentId: payload?.cashFlowDocumentIds?.[0],
      additionalDocumentIds: payload?.additionalDocumentIds || [],
      isNotMandatoryApg: !payload?.isApgApplicable,
      apgAmountMode: payload?.apgAmountMode,
      apgPercent: payload?.apgAmountMode === "PERCENT" ? apgPercentNum : null,
      apgFixedAmount: payload?.apgAmountMode === "FIXED" ? apgFixedAmountNum : null,
      apgStartDate: toEpochMs(payload?.apgStartDate),
      apgEndDate: toEpochMs(payload?.apgEndDate),
      apgExpectedPaymentDate: toEpochMs(payload?.expectedAdvancePaymentDate),
      isNotMandatoryPbg: !payload?.isPbgApplicable,
      pbgAmountMode: payload?.pbgAmountMode,
      pbgPercent: payload?.pbgAmountMode === "PERCENT" ? pbgPercentNum : null,
      pbgFixedAmount: payload?.pbgAmountMode === "FIXED" ? pbgFixedAmountNum : null,
      pbgExpiryMode: payload?.pbgExpiryMode,
      defectsLiabilityPeriodMonths: defectsLiabilityPeriodMonthsNum,
      pbgStartDate: toEpochMs(payload?.pbgStartDate),
      pbgEndDate: pbgEndDate,
      uploadApgLetterDocumentId: payload?.uploadApgLetterDocumentId ?? null,
      uploadPbgLetterDocumentId: payload?.uploadPbgLetterDocumentId ?? null,
      status: payload?.status ?? null,
      projectApprovalDate: payload?.projectApprovalDate ?? null,
      zeloBgIssuanceDate: payload?.zeloBgIssuanceDate ?? null,
      advancePaymentCashSweepPercent: payload?.advancePaymentCashSweepPercent ?? null,
      invoicePaymentCashSweepPercent: payload?.invoicePaymentCashSweepPercent ?? null,
      initialCollectedCashMarginPercent: payload?.initialCollectedCashMarginPercent ?? null,
      platformFeePercentPerDay: payload?.platformFeePercentPerDay ?? null,
      fdAmount: null,
      fdStartDate: null,
      fdMaturityDate: null,
      fdInterestRate: null,
      fdIssuedDate: null,
      fdBankName: null,
    };

    if (editProject && bankGuaranteeDetails.projectId) {
      payload.id = bankGuaranteeDetails.projectId;
    }
    
    // Ensure additionalDocumentIds is an array of numbers
    if (payload.additionalDocumentIds && Array.isArray(payload.additionalDocumentIds)) {
      payload.additionalDocumentIds = payload.additionalDocumentIds.map(id => 
        typeof id === 'number' ? id : parseInt(id, 10)
      ).filter(id => !isNaN(id));
    }
    
    console.log("=== Final Payload ===");
    console.log("Bank Guarantee Create Payload:", payload);
    console.log("Additional Document IDs in payload:", payload.additionalDocumentIds);
    console.log("Additional Document IDs type:", typeof payload.additionalDocumentIds);
    console.log("Additional Document IDs length:", payload.additionalDocumentIds?.length);
    
    setLoadings((prev) => ({ ...prev, submit: true }));
    try {
      // Use update endpoint if editing, create endpoint if new
      const isEdit = editProject && bankGuaranteeDetails.projectId;
      const endpoint = isEdit
        ? (skipLayout
            ? `/bankGuarantee/v1/update`
            : `/bankGuarantee/v1/update/${bankGuaranteeDetails.projectId}`)
        : `/bankGuarantee/v1/create`;
      const method = isEdit ? "put" : "post";
      
      const response = await Api(method, endpoint, payload);
      if (response && response.success) {
        setBankGuaranteeDetails(initialBankGuaranteeDetails);
        alert(editProject ? "Bank Guarantee Project updated successfully!" : "Bank Guarantee Project created successfully!");
        if (!skipLayout) {
          navigate(-1);
        } else if (onBack) {
          onBack();
        }
      } else {
        if (response.message) alert(response.message);
        else alert(editProject ? "Error updating Bank Guarantee Project. Please try again." : "Error creating Bank Guarantee Project. Please try again.");
      }
    } catch (error) {
      alert(editProject ? "Error updating form. Please try again." : "Error submitting form. Please try again.");
    } finally {
      setLoadings((prev) => ({ ...prev, submit: false }));
    }
  };
  return (
    <>
      {!skipLayout && <Layout />}
      <div 
        id={skipLayout ? "container-2" : "container"} 
        className={skipLayout ? "deshboard-content" : "mid-content-container"}
        style={skipLayout ? { marginInlineStart: "0px", backgroundColor: "none !important" } : {}}
      >
        <div className="bank-guarantee-section">
          <div className="container" style={{ maxWidth: "100%" }}>
            <div className="row">
              <div className="col-12">
                {skipLayout && onBack && (
                  <Button
                    variant="link"
                    onClick={onBack}
                    className="mb-3 p-0"
                    style={{ textDecoration: "none", padding: 0 }}
                  >
                    ← Back to Products
                  </Button>
                )}
                <h2>Bank Guarantee Project</h2>
                <p className="m-0">
                  Tell us about your project so we can create your bank
                  guarantees.
                </p>
                {skipLayout && (
                  <div className="bg-warning-box mt-3">
                    <div className="bg-warning-icon">
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <div className="bg-warning-text">
                      To add a Bank Guarantee Project, the related Buyer Platform (Counterparty) must already be added for Invoice Financing. If you haven't added the buyer platform yet, please do so first before creating a Bank Guarantee project.
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="project-detail-section mt-4 pt-2">
              <Form className="form-box-section">
                {loadings.submit ? (
                  <LoaderModal />
                ) : (
                  <>
                    <h3 className="mb-3">Project Details</h3>
                    <div className="project-detail-box p-4">
                      <Row className="mb-4 pb-2">
                        <Form.Group as={Col} controlId="formProjectName">
                          <Form.Label>
                            Project Name{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="projectNameTooltip" />
                            <ReactTooltip 
                              id="projectNameTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              Enter a clear and identifiable name for your project. This will be used to reference the bank guarantee throughout your application.
                            </ReactTooltip>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter Project Name"
                            name="projectName"
                            value={bankGuaranteeDetails.projectName}
                            onChange={handleInputChange}
                            minLength={2}
                            maxLength={255}
                          />
                        </Form.Group>
                        <Form.Group as={Col} controlId="formProjectReferenceNumber">
                          <Form.Label>
                            Project Reference Number {" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="projectReferenceNumberTooltip" />
                            <ReactTooltip 
                              id="projectReferenceNumberTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              Enter the official project reference number mentioned in your agreement, contract, or LOA (if applicable). This helps us identify your project accurately.
                            </ReactTooltip>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter Project Reference Number"
                            name="projectReferenceNumber"
                            value={bankGuaranteeDetails.projectReferenceNumber}
                            onChange={handleInputChange}
                            maxLength={256}
                          />
                        </Form.Group>
                        <Form.Group as={Col} controlId="formProjectValue">
                          <Form.Label>
                            Project Value (AED){" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="projectValueTooltip" />
                            <ReactTooltip 
                              id="projectValueTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              Total contract value for this project in AED. This should match the amount mentioned in your agreement with the buyer/contracting authority.
                            </ReactTooltip>
                          </Form.Label>
                          <NumberFormat
                            customInput={Form.Control}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            placeholder="Enter Project Value"
                            name="projectValue"
                            value={bankGuaranteeDetails.projectValue}
                            onValueChange={(values) => {
                              setBankGuaranteeDetails((prev) => ({
                                ...prev,
                                projectValue: values.value, // unformatted numeric string (payload-safe)
                              }));
                            }}
                            className="custom-input"
                          />
                          <div className="note-btm">Numeric, in AED</div>
                        </Form.Group>
                        <Form.Group as={Col} controlId="formCounterparty">
                          <Form.Label>
                            Counterparty{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="counterpartyTooltip" />
                            <ReactTooltip 
                              id="counterpartyTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              Select the buyer platform or government entity for whom this bank guarantee is being issued. Only buyer platforms linked to your account will appear here.
                            </ReactTooltip>
                          </Form.Label>
                          <AutocompleteSelect
                            inputId="formCounterparty"
                            name="counterparty"
                            options={counterpartyOptions}
                            getOptionValue={(option) => option.id}
                            getOptionLabel={(option) => option.name}
                            value={bankGuaranteeDetails.counterparty}
                            onChange={(counterparty) =>
                              handleInputChange({ target: { name: "counterparty", value: counterparty } })
                            }
                            placeholder="Select Counterparty..."
                            emptyMessage="No counterparties available"
                            noMatchMessage="No matching counterparty"
                            showIndicator={false}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-4 pb-2">
                        <Form.Group as={Col} md={4} controlId="formProjectType">
                          <Form.Label>Project Type</Form.Label>
                          <div className="d-flex align-items-center gap-3">
                            <span style={{ fontWeight: 500 }}>
                              {bankGuaranteeDetails.isOpenEnded ? "Open Ended" : "Closed Ended"}
                            </span>
                            <Switch
                              className="switch-input-size"
                              value={bankGuaranteeDetails.isOpenEnded ? 1 : 0}
                              onChange={(val) =>
                                setBankGuaranteeDetails((prev) => ({
                                  ...prev,
                                  isOpenEnded: !!val,
                                }))
                              }
                            />
                          </div>
                          <div className="note-btm mt-2">
                            {bankGuaranteeDetails.isOpenEnded
                              ? "Project does not have a predefined end date."
                              : "Project has a defined end date."}
                          </div>
                        </Form.Group>
                        <Form.Group as={Col} controlId="formGridStartDate">
                          <Form.Label>
                            Project Start Date{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="projectStartDateTooltip" />
                            <ReactTooltip 
                              id="projectStartDateTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              The official start date of the project or contract. Used to define your project timeline.
                            </ReactTooltip>
                          </Form.Label>
                          <input
                            type="date"
                            className="custom-date-input"
                            placeholder="Select Start Date or type YYYY-MM-DD"
                            name="projectStartDate"
                            value={bankGuaranteeDetails?.projectStartDate}
                            onChange={handleInputChange}
                            onFocus={(e) => e.target.select()}
                            title="You can type the date directly in YYYY-MM-DD format or use the calendar picker"
                          />
                        </Form.Group>

                        <Form.Group as={Col} controlId="formGridEndDate">
                          <Form.Label>
                            Project End Date{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="projectEndDateTooltip" />
                            <ReactTooltip 
                              id="projectEndDateTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              The expected completion date of the project as per your contract.
                            </ReactTooltip>
                          </Form.Label>
                          <input
                            type="date"
                            className="custom-date-input"
                            placeholder="Select End Date or type YYYY-MM-DD"
                            name="projectEndDate"
                            value={bankGuaranteeDetails.projectEndDate}
                            onChange={handleInputChange}
                            onFocus={(e) => e.target.select()}
                            title="You can type the date directly in YYYY-MM-DD format or use the calendar picker"
                            min={
                              bankGuaranteeDetails?.projectStartDate
                                ? moment(bankGuaranteeDetails?.projectStartDate).format("YYYY-MM-DD")
                                : undefined
                            }
                            required={!bankGuaranteeDetails.isOpenEnded}
                          />
                          <div className="note-btm mt-2">
                            {bankGuaranteeDetails.isOpenEnded
                              ? "Optional for Open Ended projects."
                              : "Mandatory for Closed Ended projects."}
                          </div>
                        </Form.Group>
                      </Row>

                      <Row>
                        <Form.Group as={Col} controlId="formAgreement">
                          <Form.Label>
                            Agreement Document Upload{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="agreementDocumentTooltip" />
                            <ReactTooltip 
                              id="agreementDocumentTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              Upload the signed project agreement or contract. This helps our team verify project details. Maximum 50 MB.
                            </ReactTooltip>
                          </Form.Label>
                          <div
                            className="license-upload-section d-flex flex-column align-items-center justify-content-center p-4"
                            style={{ borderStyle: "dashed" }}
                          >
                            {loadings.agreementDocument && (
                              <div>
                                <LoaderModal />
                              </div>
                            )}
                            {bankGuaranteeDetails.agreementDocument.length ===
                              0 && (
                              <div
                                className="d-flex flex-column align-items-center justify-content-center"
                                onClick={() =>
                                  document
                                    .getElementById("agreementFile")
                                    .click()
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <img src={UploadDoc} alt="Upload Icon" />
                                <p className="mb-1">Upload Document</p>
                                <small>(PDF, DOCX supported)</small>
                                <input
                                  id="agreementFile"
                                  type="file"
                                  accept=".pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                  hidden
                                  onChange={(e) =>
                                    handleFileUpload(e, "agreementDocument")
                                  }
                                />
                              </div>
                            )}
                            {!loadings.agreementDocument &&
                              bankGuaranteeDetails.agreementDocument.length >
                                0 && (
                                <div className="uploaded-files-list mt-3 w-100">
                                  {bankGuaranteeDetails.agreementDocument.map(
                                    (file, index) => (
                                      <div
                                        key={index}
                                        className="d-flex flex-column justify-content-between align-items-center mb-2 p-2"
                                      >
                                        <span className="file-name text-truncate">
                                          {file.name}
                                        </span>
                                        <span className="d-flex gap-3">
                                          <Button
                                            variant="link"
                                            className="p-0"
                                            onClick={() => handlePreview(file)}
                                          >
                                            <FaEye size={20} />
                                          </Button>
                                          <Button
                                            variant="link"
                                            className="p-0"
                                            onClick={() => {
                                              handleDeleteFile(
                                                file,
                                                "agreementDocument"
                                              );
                                            }}
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="16"
                                              height="16"
                                              fill="currentColor"
                                              className="bi bi-trash3"
                                              viewBox="0 0 16 16"
                                              color="red"
                                            >
                                              <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                                            </svg>
                                          </Button>
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </Form.Group>

                        <Form.Group as={Col} controlId="formExcel">
                          <Form.Label>
                            Project Cash Flows{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="cashFlowDocumentTooltip" />
                            <ReactTooltip 
                              id="cashFlowDocumentTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              Upload cash flow details that show expected payment timelines for the project. Maximum 50 MB.
                            </ReactTooltip>
                          </Form.Label>
                          <div
                            className="license-upload-section d-flex flex-column align-items-center justify-content-center p-4"
                            style={{ borderStyle: "dashed" }}
                          >
                            {loadings.cashFlowDocument && <LoaderModal />}
                            {bankGuaranteeDetails?.cashFlowDocument?.length ===
                              0 && (
                              <div
                                style={{ cursor: "pointer" }}
                                className="d-flex flex-column align-items-center justify-content-center"
                                onClick={() =>
                                  document
                                    .getElementById("cashFlowFile")
                                    .click()
                                }
                              >
                                <img src={UploadDoc} alt="Upload Icon" />
                                <p className="mb-1">Upload Document</p>
                                <small>(XLS, XLSX supported)</small>
                                <input
                                  id="cashFlowFile"
                                  type="file"
                                  accept=".xls,.xlsx"
                                  hidden
                                  onChange={(e) =>
                                    handleFileUpload(e, "cashFlowDocument")
                                  }
                                />
                              </div>
                            )}
                            {!loadings.cashFlowDocument &&
                              bankGuaranteeDetails.cashFlowDocument.length >
                                0 && (
                                <div className="uploaded-files-list mt-3 w-100">
                                  {bankGuaranteeDetails.cashFlowDocument.map(
                                    (file, index) => (
                                      <div
                                        key={index}
                                        className="d-flex flex-column justify-content-between align-items-center mb-2 p-2"
                                      >
                                        <span className="file-name text-truncate">
                                          {file.name}
                                        </span>
                                        <span className="d-flex gap-3">
                                          <Button
                                            variant="link"
                                            className="p-0"
                                            onClick={() => handlePreview(file)}
                                          >
                                            <FaEye size={20} />
                                          </Button>
                                          <Button
                                            variant="link"
                                            className="p-0"
                                            onClick={() => {
                                              handleDeleteFile(
                                                file,
                                                "cashFlowDocument"
                                              );
                                            }}
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="16"
                                              height="16"
                                              fill="currentColor"
                                              className="bi bi-trash3"
                                              viewBox="0 0 16 16"
                                              color="red"
                                            >
                                              <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                                            </svg>
                                          </Button>
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </Form.Group>
                      </Row>

                      {/* Additional Documents Section */}
                      <Row className="mt-4">
                        <Form.Group as={Col} controlId="formAdditionalDocuments">
                          <Form.Label>
                            Additional Supporting Documents{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="additionalDocumentsTooltip" />
                            <ReactTooltip 
                              id="additionalDocumentsTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              Upload any relevant documents that can support project evaluation (e.g., scope of work, BOQ, award letters). PDF/JPG/PNG formats supported. Max 5 files, 50 MB per file.
                            </ReactTooltip>
                          </Form.Label>
                          <div
                            className="license-upload-section d-flex flex-column align-items-center justify-content-center p-4"
                            style={{ borderStyle: "dashed" }}
                          >
                            <input
                              id="additionalDocumentsFile"
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              multiple
                              hidden
                              onChange={(e) =>
                                handleFileUpload(e, "additionalDocuments")
                              }
                            />
                            {loadings.additionalDocuments && <LoaderModal />}
                            {!loadings.additionalDocuments && bankGuaranteeDetails?.additionalDocuments?.length === 0 && (
                              <div
                                style={{ cursor: "pointer" }}
                                className="d-flex flex-column align-items-center justify-content-center"
                                onClick={() =>
                                  document
                                    .getElementById("additionalDocumentsFile")
                                    .click()
                                }
                              >
                                <img src={UploadDoc} alt="Upload Icon" />
                                <p className="mb-1">Upload Document</p>
                                <small>(PDF, JPG, PNG supported - Max 5 files, 50 MB per file)</small>
                              </div>
                            )}
                            {!loadings.additionalDocuments &&
                              bankGuaranteeDetails.additionalDocuments.length > 0 && (
                                <div className="uploaded-files-list mt-3 w-100">
                                  {bankGuaranteeDetails.additionalDocuments.map(
                                    (file, index) => (
                                      <div
                                        key={index}
                                        className="d-flex flex-column justify-content-between align-items-center mb-2 p-2"
                                      >
                                        <span className="file-name text-truncate">
                                          {file.name}
                                        </span>
                                        <span className="d-flex gap-3">
                                          <Button
                                            variant="link"
                                            className="p-0"
                                            onClick={() => handlePreview(file)}
                                          >
                                            <FaEye size={20} />
                                          </Button>
                                          <Button
                                            variant="link"
                                            className="p-0"
                                            onClick={() => {
                                              handleDeleteFile(
                                                file,
                                                "additionalDocuments"
                                              );
                                            }}
                                          >
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="16"
                                              height="16"
                                              fill="currentColor"
                                              className="bi bi-trash3"
                                              viewBox="0 0 16 16"
                                              color="red"
                                            >
                                              <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                                            </svg>
                                          </Button>
                                        </span>
                                      </div>
                                    )
                                  )}
                                  {bankGuaranteeDetails.additionalDocuments.length < 5 && (
                                    <div
                                      style={{ cursor: "pointer", marginTop: "10px" }}
                                      className="d-flex align-items-center justify-content-center"
                                      onClick={() =>
                                        document
                                          .getElementById("additionalDocumentsFile")
                                          .click()
                                      }
                                    >
                                      <span style={{ color: "#0a58ca", textDecoration: "underline" }}>
                                        + Add More Documents ({bankGuaranteeDetails.additionalDocuments.length}/5)
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                          <div className="note-btm mt-2">
                            Upload any document that will help with project evaluation.
                          </div>
                          <div className="note-btm mt-2">
                          Please download the NOC template below. The sections highlighted in the template require your input, kindly complete the highlighted fields and upload the completed NOC here.
                          </div>
                          <div className="mt-2">
                            <a
                              href="/NOC.pdf"
                              download
                              className="btn btn-outline-primary btn-sm"
                              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                            >
                              <IoDownload />
                              Download NOC Template (PDF)
                            </a>
                          </div>
                        </Form.Group>
                      </Row>
                    </div>
                    <h3 className="mt-4 pt-2 mb-3">
                      Bank Guarantee Type Identification (Select at least one or
                      both)
                    </h3>
                    <div className="project-detail-box p-4">
                      <div className="d-flex align-items-center switch-input-section mb-3">
                        <h3 className="me-2 mb-0">
                          Is Advance Payment Guarantee (APG) Applicable?
                        </h3>
                        <Switch
                          className="switch-input-size"
                          value={bankGuaranteeDetails.isApgApplicable ? 1 : 0}
                          onChange={(val) =>
                            handleSwitchChange("isApgApplicable", !!val)
                          }
                        />
                      </div>
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group controlId="formApgAmountMode" className="mb-3">
                            <Form.Label>APG Amount</Form.Label>
                            <div className="d-flex flex-wrap gap-3 mb-2 bg-segment-radio">
                              <Form.Check
                                type="radio"
                                id="apgModePercent"
                                name="apgAmountMode"
                                label="Percentage Project Value"
                                checked={bankGuaranteeDetails.apgAmountMode === "PERCENT"}
                                onChange={() =>
                                  setBankGuaranteeDetails((prev) => ({
                                    ...prev,
                                    apgAmountMode: "PERCENT",
                                    apgFixedAmount: "",
                                  }))
                                }
                                disabled={!bankGuaranteeDetails.isApgApplicable}
                                className="form-check-inline segment-radio-item"
                              />
                              <Form.Check
                                type="radio"
                                id="apgModeFixed"
                                name="apgAmountMode"
                                label="Fixed Amount"
                                checked={bankGuaranteeDetails.apgAmountMode === "FIXED"}
                                onChange={() =>
                                  setBankGuaranteeDetails((prev) => ({
                                    ...prev,
                                    apgAmountMode: "FIXED",
                                    apgPercentage: "",
                                  }))
                                }
                                disabled={!bankGuaranteeDetails.isApgApplicable}
                                className="form-check-inline segment-radio-item"
                              />
                            </div>

                            {bankGuaranteeDetails.apgAmountMode === "PERCENT" ? (
                              <>
                                <Row className="g-2 align-items-end mb-2">
                                  <Col md={8}>
                                    <Form.Label className="mb-1">
                                      Enter Percentage{" "}
                                      <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="apgPercentageTooltip" />
                                      <ReactTooltip id="apgPercentageTooltip" place="top" effect="solid" className="bg-tooltip-custom">
                                        Percentage of the project value that needs to be covered under the Advance Payment Guarantee (APG). Provided by the counterparty.
                                      </ReactTooltip>
                                    </Form.Label>
                                    <div className="d-flex align-items-center gap-2 pe-2 rounded-3" style={{backgroundColor:"aliceblue"}}>
                                      <Form.Control
                                        type="number"
                                        name="apgPercentage"
                                        placeholder="Enter %"
                                        value={bankGuaranteeDetails.apgPercentage}
                                        onChange={handleInputChange}
                                        className="custom-input"
                                        min="0"
                                        pattern="[0-9]*"
                                        onKeyDown={(e) => {
                                          if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
                                        }}
                                        disabled={!bankGuaranteeDetails.isApgApplicable}
                                      />
                                      <span className="d-flex" style={{minInlineSize:"fit-content"}}> Calculated: {
                                        bankGuaranteeDetails.isApgApplicable &&
                                        bankGuaranteeDetails.projectValue &&
                                        bankGuaranteeDetails.apgPercentage
                                          ? (
                                              Number(bankGuaranteeDetails.projectValue) *
                                              (Number(bankGuaranteeDetails.apgPercentage) / 100)
                                            ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                          : "0"
                                      }</span>
                                    </div>
                                  </Col>
                                  {/* <Col md={4}>
                                    <Form.Label className="mb-1">Calculated</Form.Label>
                                    <Form.Control
                                      type="text"
                                      readOnly
                                      className="custom-input bg-light"
                                      value={
                                        bankGuaranteeDetails.isApgApplicable &&
                                        bankGuaranteeDetails.projectValue &&
                                        bankGuaranteeDetails.apgPercentage
                                          ? (
                                              Number(bankGuaranteeDetails.projectValue) *
                                              (Number(bankGuaranteeDetails.apgPercentage) / 100)
                                            ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                          : ""
                                      }
                                      placeholder="—"
                                    />
                                  </Col> */}
                                </Row>
                                <div className="note-btm mt-1 mb-2">
                                  If Percentage value is selected, Project Value becomes mandatory and amount is auto-calculated.
                                </div>
                              </>
                            ) : (
                              <>
                                <Row className="g-2 mb-2">
                                  <Col md={8}>
                                    <Form.Label className="mb-1">APG Fixed Amount (AED)</Form.Label>
                                    <NumberFormat
                                      customInput={Form.Control}
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      allowNegative={false}
                                      placeholder="Enter Amount"
                                      value={bankGuaranteeDetails.apgFixedAmount}
                                      disabled={!bankGuaranteeDetails.isApgApplicable}
                                      onValueChange={(values) =>
                                        setBankGuaranteeDetails((prev) => ({ ...prev, apgFixedAmount: values.value }))
                                      }
                                      className="custom-input"
                                    />
                                  </Col>
                                </Row>
                                <div className="note-btm mt-1 mb-2">
                                  If Fixed selected, Project Value is not mandatory.
                                </div>
                              </>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          <Form.Group controlId="formAdvanceDate" className="mb-3">
                            <Form.Label>
                              Expected Advance Payment Date{" "}
                              <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="expectedAdvancePaymentDateTooltip" />
                              <ReactTooltip id="expectedAdvancePaymentDateTooltip" place="top" effect="solid" className="bg-tooltip-custom">
                                The date on which you expect to receive the advance payment from the counterparty.
                              </ReactTooltip>
                            </Form.Label>
                            <input
                              type="date"
                              className="custom-date-input"
                              name="expectedAdvancePaymentDate"
                              value={bankGuaranteeDetails.expectedAdvancePaymentDate}
                              onChange={handleInputChange}
                              onFocus={(e) => e.target.select()}
                              title="You can type the date directly in YYYY-MM-DD format or use the calendar picker"
                              min={
                                bankGuaranteeDetails?.projectStartDate
                                  ? moment(bankGuaranteeDetails?.projectStartDate).format("YYYY-MM-DD")
                                  : moment().format("YYYY-MM-DD")
                              }
                              disabled={!bankGuaranteeDetails.isApgApplicable}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex align-items-center switch-input-section mb-4 mt-2">
                        <h3 className="me-2 mb-0">
                          Is Performance Bank Guarantee (PPG) Applicable?
                        </h3>
                        <Switch
                          className="switch-input-size"
                          value={bankGuaranteeDetails.isPbgApplicable ? 1 : 0}
                          onChange={(val) =>
                            handleSwitchChange("isPbgApplicable", !!val)
                          }
                        />
                      </div>

                      <Row className="mb-4 pb-2 g-3">
                        <Col md={8}>
                          <Form.Group controlId="formPbgAmountMode" className="mb-3">
                            <Form.Label>PBG Amount</Form.Label>
                            <div className="d-flex flex-wrap gap-3 mb-2 bg-segment-radio">
                              <Form.Check
                                type="radio"
                                id="pbgModePercent"
                                name="pbgAmountMode"
                                label="Percentage Project Value"
                                style={{width:"20px !important"}}
                                checked={bankGuaranteeDetails.pbgAmountMode === "PERCENT"}
                                onChange={() =>
                                  setBankGuaranteeDetails((prev) => ({
                                    ...prev,
                                    pbgAmountMode: "PERCENT",
                                    pbgFixedAmount: "",
                                  }))
                                }
                                disabled={!bankGuaranteeDetails.isPbgApplicable}
                                className="form-check-inline segment-radio-item"
                              />
                              <Form.Check
                                type="radio"
                                id="pbgModeFixed"
                                name="pbgAmountMode"
                                label="PBG Fixed Amount"
                                style={{width:"20px !important"}}
                                checked={bankGuaranteeDetails.pbgAmountMode === "FIXED"}
                                onChange={() =>
                                  setBankGuaranteeDetails((prev) => ({
                                    ...prev,
                                    pbgAmountMode: "FIXED",
                                    pbgPercentage: "",
                                  }))
                                }
                                disabled={!bankGuaranteeDetails.isPbgApplicable}
                                className="form-check-inline segment-radio-item"
                              />
                            </div>

                            {bankGuaranteeDetails.pbgAmountMode === "PERCENT" ? (
                              <>
                                <Row className="g-2 align-items-end mb-2">
                                  <Col md={12}>
                                    <Form.Label className="mb-1">
                                      Enter Percentage{" "}
                                      <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="pbgPercentageTooltip" />
                                      <ReactTooltip id="pbgPercentageTooltip" place="top" effect="solid" className="bg-tooltip-custom">
                                        Percentage of the project value required under the Performance Bank Guarantee (PBG).
                                      </ReactTooltip>
                                    </Form.Label>
                                    <div className="d-flex align-items-center gap-2 pe-2 rounded-3" style={{backgroundColor:"aliceblue"}}>
                                      <Form.Control
                                        type="number"
                                        name="pbgPercentage"
                                        placeholder="Enter %"
                                        value={bankGuaranteeDetails.pbgPercentage}
                                        onChange={handleInputChange}
                                        className="custom-input"
                                        min="0"
                                        pattern="[0-9]*"
                                        onKeyDown={(e) => {
                                          if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
                                        }}
                                        disabled={!bankGuaranteeDetails.isPbgApplicable}
                                      />
                                      <span className="d-flex" style={{minInlineSize:"fit-content"}}> Calculated: {
                                        bankGuaranteeDetails.isPbgApplicable &&
                                        bankGuaranteeDetails.projectValue &&
                                        bankGuaranteeDetails.pbgPercentage
                                          ? (
                                              Number(bankGuaranteeDetails.projectValue) *
                                              (Number(bankGuaranteeDetails.pbgPercentage) / 100)
                                            ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                          : "0"
                                      }</span>
                                    </div>
                                  </Col>
                                  {/* <Col md={6}>
                                    <Form.Label className="mb-1">Calculated</Form.Label>
                                    <Form.Control
                                      type="text"
                                      readOnly
                                      className="custom-input bg-light"
                                      value={
                                        bankGuaranteeDetails.isPbgApplicable &&
                                        bankGuaranteeDetails.projectValue &&
                                        bankGuaranteeDetails.pbgPercentage
                                          ? (
                                              Number(bankGuaranteeDetails.projectValue) *
                                              (Number(bankGuaranteeDetails.pbgPercentage) / 100)
                                            ).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                          : ""
                                      }
                                      placeholder="—"
                                    />
                                  </Col> */}
                                </Row>
                                <div className="note-btm mt-1 mb-2">
                                  If Percentage value is selected, Project Value becomes mandatory and amount is auto-calculated.
                                </div>
                              </>
                            ) : (
                              <>
                                <Row className="g-2 mb-2">
                                  <Col md={12}>
                                    <Form.Label className="mb-1">PBG Fixed Amount (AED)</Form.Label>
                                    <NumberFormat
                                      customInput={Form.Control}
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      allowNegative={false}
                                      placeholder="Enter Amount"
                                      value={bankGuaranteeDetails.pbgFixedAmount}
                                      disabled={!bankGuaranteeDetails.isPbgApplicable}
                                      onValueChange={(values) =>
                                        setBankGuaranteeDetails((prev) => ({ ...prev, pbgFixedAmount: values.value }))
                                      }
                                      className="custom-input"
                                    />
                                  </Col>
                                </Row>
                                <div className="note-btm mt-1 mb-2">
                                  If Fixed selected, Project Value is not mandatory.
                                </div>
                              </>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={8}>
                          <Form.Group controlId="formPbgExpiryMode" className="mb-3">
                            <Form.Label>
                              PBG Expiry Date Configuration{" "}
                              <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="pbgExpiryModeTooltip" />
                              <ReactTooltip id="pbgExpiryModeTooltip" place="top" effect="solid" className="bg-tooltip-custom">
                                PBG expiry date can either be manually defined or auto-calculated based on Defects Liability Period (DLP) from the Project End Date.
                              </ReactTooltip>
                            </Form.Label>
                            <div className="d-flex flex-wrap gap-3 mb-2 bg-segment-radio">
                              <Form.Check
                                type="radio"
                                id="pbgExpiryManual"
                                name="pbgExpiryMode"
                                label="Manual Date"
                                checked={bankGuaranteeDetails.pbgExpiryMode === "MANUAL"}
                                onChange={() =>
                                  setBankGuaranteeDetails((prev) => ({
                                    ...prev,
                                    pbgExpiryMode: "MANUAL",
                                    defectsLiabilityPeriodMonths: "",
                                    pbgEndDate: prev.pbgEndDate,
                                  }))
                                }
                                disabled={!bankGuaranteeDetails.isPbgApplicable}
                                className="form-check-inline segment-radio-item"
                              />
                              <Form.Check
                                type="radio"
                                id="pbgExpiryDlp"
                                name="pbgExpiryMode"
                                label="Liability Period"
                                checked={bankGuaranteeDetails.pbgExpiryMode === "DLP"}
                                onChange={() =>
                                  setBankGuaranteeDetails((prev) => ({
                                    ...prev,
                                    pbgExpiryMode: "DLP",
                                    pbgEndDate: "",
                                  }))
                                }
                                disabled={!bankGuaranteeDetails.isPbgApplicable}
                                className="form-check-inline segment-radio-item"
                              />
                            </div>

                            {bankGuaranteeDetails.pbgExpiryMode === "DLP" && (
                              <>
                                <Form.Label className="mt-2 mb-1">Liability Period (Months)</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="defectsLiabilityPeriodMonths"
                                  placeholder="e.g. 12"
                                  value={bankGuaranteeDetails.defectsLiabilityPeriodMonths}
                                  onChange={handleInputChange}
                                  className="custom-input"
                                  disabled={!bankGuaranteeDetails.isPbgApplicable}
                                />
                              </>
                            )}
                          </Form.Group>
                        </Col>

                        <Form.Group as={Col} md={8} controlId="formPbgEndDate">
                          <Form.Label>
                            PBG End Date{" "}
                            <FontAwesomeIcon icon={faInfoCircle} color="var(--bg-theme-light)" style={{ width: "10px", height: "10px", marginBlockEnd: "2px" }} data-tip data-for="pbgEndDateTooltip" />
                            <ReactTooltip 
                              id="pbgEndDateTooltip" 
                              place="top" 
                              effect="solid"
                              className="bg-tooltip-custom"
                            >
                              The expiry date of the PBG, typically at project completion or defect liability period end.
                            </ReactTooltip>
                          </Form.Label>
                          {bankGuaranteeDetails.pbgExpiryMode === "DLP" ? (
                            <Form.Control
                              type="text"
                              readOnly
                              value={
                                bankGuaranteeDetails.projectEndDate &&
                                bankGuaranteeDetails.defectsLiabilityPeriodMonths
                                  ? moment(bankGuaranteeDetails.projectEndDate)
                                      .add(
                                        Number(bankGuaranteeDetails.defectsLiabilityPeriodMonths || 0),
                                        "months"
                                      )
                                      .format("DD/MM/YYYY")
                                  : ""
                              }
                              placeholder="Auto-calculated from Project End Date + DLP"
                              className="custom-input"
                            />
                          ) : (
                            <input
                              type="date"
                              className="custom-date-input"
                              name="pbgEndDate"
                              value={bankGuaranteeDetails.pbgEndDate}
                              onChange={handleInputChange}
                              onFocus={(e) => e.target.select()}
                              title="You can type the date directly in YYYY-MM-DD format or use the calendar picker"
                              min={
                                bankGuaranteeDetails?.projectStartDate
                                  ? moment(bankGuaranteeDetails.projectStartDate).format("YYYY-MM-DD")
                                  : undefined
                              }
                              disabled={!bankGuaranteeDetails.isPbgApplicable}
                            />
                          )}
                          {bankGuaranteeDetails.pbgExpiryMode === "DLP" && (
                            <div className="note-btm mt-2">
                              If DLP is selected, Project End Date is required.
                            </div>
                          )}
                        </Form.Group>
                      </Row>
                    </div>
                    <div className="bank-guarantee-checked position-relative mt-4 pt-2 mb-5 pb-4">
                      {/* <Form.Check
                        type="checkbox"
                        id="agreeToTerms1"
                        checked={bankGuaranteeDetails.agreeToTerms1}
                        onChange={handleCheckboxChange}
                        className="d-flex align-items-start mb-3"
                        label="I understand I am signing the above agreements electronically and that my electronic signature is the legal equivalent of my manual signature on these agreements."
                      /> */}
                      <Form.Check
                        type="checkbox"
                        id="agreeToTerms2"
                        checked={bankGuaranteeDetails.agreeToTerms2}
                        onChange={handleCheckboxChange}
                        className="d-flex align-items-start mb-3"
                        label="I Authorize ZeloFinance to manage guarantee issuance with partner banks on my behalf."
                      />
                      {/* <Form.Check
                        type="checkbox"
                        id="agreeToTerms3"
                        checked={bankGuaranteeDetails.agreeToTerms3}
                        onChange={handleCheckboxChange}
                        className="d-flex align-items-start mb-3"
                        label="I authorize ZeloFinance to collect payments on my behalf."
                      /> */}
                    </div>{" "}
                  </>
                )}
                <div className={!skipLayout ? "f-btn-section px-4 py-3 d-flex position-fixed justify-content-end" : "f-btn-section px-4 py-3 d-flex justify-content-end w-100 rounded-2"}>
                  <Button
                    variant="primary"
                    type="submit"
                    className="cancle-btn py-3 px-4"
                    onClick={(e) => handleCancel(e)}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!isValidForm || loadings.submit}
                    variant="primary"
                    type="submit"
                    className={`py-3 px-4 ${
                      isValidForm ? "bg-green-btn" : "btn-invalid"
                    } `}
                    onClick={handleSubmit}
                  >
                    {skipLayout ? "Save" : "Save & Continue"}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => {
          setShowPreview(false);
          // Revoke object URL to avoid memory leaks
          if (currentPreview) {
            URL.revokeObjectURL(currentPreview);
          }
        }}
        style={{ justifyContent: "space-between" }}
        size="lg"
        backdrop="static"
        className="Upload-trade-license"
        keyboard={false}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton className="justify-content-between">
          <Modal.Title className="text-truncate" style={{ maxWidth: 500 }}>
            {/* Show filename if possible */}
            {currentPreview?.name || "Preview"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="text-center"
          style={{ maxHeight: "80vh", overflow: "auto" }}
        >
          {" "}
          {loadings.preview ? (
            <div style={{ minHeight: "200px" }}>
              <LoaderModal />
            </div>
          ) : (
            <div>
              {currentPreview?.name?.endsWith(".xls") ||
              currentPreview?.name?.endsWith(".xlsx") ||
              currentPreview?.name?.endsWith(".docx") ? (
                <a
                  href={currentPreview?.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "black",
                    textDecoration: "none",
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {currentPreview?.name?.endsWith(".docx")
                    ? "Download Word File"
                    : "Download Excel File"}{" "}
                  <FaDownload color="#0a58ca" />
                </a>
              ) : currentPreview?.name?.endsWith(".pdf") ? (
                <iframe
                  src={currentPreview?.url}
                  title="PDF Preview"
                  width="100%"
                  height="600px"
                  style={{ border: "none" }}
                ></iframe>
              ) : (
                <img
                  src={currentPreview?.url}
                  alt="Preview"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              )}

              {errors.preview && <div>{errors.preview}</div>}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BankGuarantee;
